import React, {
  createRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { useAuth0 } from "@auth0/auth0-react";
import { Box, Container, Typography } from "@mui/material";
import { RadioGroup, Radio, TextField } from "@verily-src/react-design-system";
import { StudyParticipantServiceApiClient } from "apiclient/StudyParticipantServiceApiClient";
import {
  LookupSponsorParticipantRequest,
  StudyParticipant,
  UpdateStudyParticipantRequest,
} from "generated/studyparticipant/studyparticipant_pb";
import { Footer } from "components/Footer";
import { RpcError, StatusCode } from "grpc-web";
import {
  Option,
  RadioOptions,
  EditParticipantIdReasonOptions,
} from "common/RadioOptions";
import { StepInfo, Steps, EditParticipantIdSteps } from "common/Steps";
import DuplicateParticipantFoundDialog from "./DuplicateParticipantFoundDialog";
import { FieldMask } from "google-protobuf/google/protobuf/field_mask_pb";
import ContactVerilyDialog from "components/ContactVerilyDialog";
import { ManageWatchRedirectDialog } from "./ManageWatchRedirectDialog";
import { focusOnRef } from "common/Accessibility";
import StepsHeader from "components/StepsHeader";
import HeaderValuePair from "components/HeaderValuePair";
import {
  FocusOn,
  selectStudyParticipant,
  setFocusTarget,
  setSuccessMessage,
} from "./StudyParticipantsStateSlice";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "core/AppRoutes";
import SDPErrorSnackbar from "components/SDPErrorSnackbar";
import { telemetry } from "core/TelemetryController";

// Radio button selections for the task
const editReasonSelection = new RadioOptions([
  {
    id: EditParticipantIdReasonOptions.Typo,
    label:
      "I have to fix a typo. The participant’s watch assignment won’t change.",
    nextStepId: EditParticipantIdSteps.EditParticipantId,
  },
  {
    id: EditParticipantIdReasonOptions.Error,
    label: "The ID was associated with this watch by mistake.",
    nextStepId: -1,
  },
  {
    id: EditParticipantIdReasonOptions.Other,
    label: "Other",
    nextStepId: -1,
  },
]);

// The dialog for updating a participant status
const EditParticipantId: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const appState = useAppSelector((state) => state.appState);

  // Redirect to home screen when page is refreshed.
  useEffect(() => {
    if (!appState.userConfigured) {
      navigate(AppRoutes.ROOT);
    }
  }, [appState.userConfigured, navigate]);

  const exitPage = useCallback(() => {
    dispatch(setFocusTarget(FocusOn.EditParticipantId));
    navigate(AppRoutes.SITE_PARTICIPANT);
  }, [dispatch, navigate]);

  // Get RegistryId
  const currentRegistryId = useAppSelector(
    (state) => state.userConfig.selectedRegistryId
  );

  const auth0Config = useAppSelector((state) => state.auth0Config);
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();

  const participantsViewState = useAppSelector(
    (state) => state.participantsViewState
  );

  const [studyParticipant, setStudyParticipant] = useState<StudyParticipant>(
    new StudyParticipant()
  );

  const [selectedReason, setSelectedReason] = useState<Option>(
    editReasonSelection.default
  );

  // Focus on h1 header in navigation landmark
  const [focusOnHeaderTitle, setFocusOnHeaderTitle] = useState<boolean>(true);
  // Ref to focus on h2 section title within main landmark
  const sectionRef = useRef<HTMLHeadingElement>(null);

  const [focusIndex, setFocusIndex] = useState(-1);
  const optionsRef = useRef(
    Array.from(editReasonSelection.options).map(() =>
      createRef<HTMLInputElement>()
    )
  );

  const [validateLoading, setValidateLoading] = useState<boolean>(false);
  const [editLoading, setEditLoading] = useState<boolean>(false);

  // Error snackbar fields
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [errorSnackbarMessage, setErrorSnackbarMessage] = useState("");

  const setError = useCallback((message: string) => {
    setErrorSnackbarMessage(message);
    setShowErrorSnackbar(true);
  }, []);

  useEffect(() => {
    setFocusIndex(-1);
  }, []);

  useEffect(() => {
    if (participantsViewState.studyParticipantSerialized) {
      const serialized = selectStudyParticipant(participantsViewState);
      setStudyParticipant(serialized);
    }
  }, [participantsViewState]);

  useEffect(() => {
    if (focusIndex > -1) {
      focusOnRef(optionsRef.current[focusIndex]);
    }
  }, [focusIndex]);

  const [newParticipantId, setNewParticipantId] = useState<string>("");
  const [newIdTextFieldError, setNewIdTextFieldError] =
    useState<boolean>(false);
  const [newIdTextFieldHelperText, setNewIdTextFieldHelperText] =
    useState<string>("");

  const stepInfo: Array<StepInfo> = [
    {
      id: EditParticipantIdSteps.EditReason,
      page: (
        <RadioGroup>
          <Box
            sx={{
              paddingBottom: "10px",
              width: "100%",
            }}
          >
            <Typography
              ref={sectionRef}
              tabIndex={-1}
              component="h2"
              variant="display6"
            >
              Why do you need to edit this participant’s ID?
            </Typography>
          </Box>
          {Array.from(editReasonSelection.options).map(
            ([_, option]: [number, Option], index) => {
              return (
                <Radio
                  inputRef={optionsRef.current[index]}
                  key={index}
                  label={option.label}
                  value={option.nextStepId}
                  checked={selectedReason.id === option.id}
                  onChange={() => {
                    setSelectedReason(option);
                    setFocusIndex(index);
                  }}
                />
              );
            }
          )}
        </RadioGroup>
      ),
      isPreviousDisabled: true,
      onPrevious: () => {},
      nextButtonText: "Next",
      isNextButtonDisabled: (() => {
        return selectedReason.id === -1;
      })(),
      onNext: () => {
        switch (selectedReason.id) {
          case EditParticipantIdReasonOptions.Other:
            setShowContactVerilyDialog(true);
            break;
          case EditParticipantIdReasonOptions.Error:
            setShowRedirectDialog(true);
            break;
          default:
            setPrevStepId(EditParticipantIdSteps.EditReason);
            setStepId(selectedReason.nextStepId);
            break;
        }
      },
    },
    {
      id: EditParticipantIdSteps.EditParticipantId,
      page: (
        <>
          <Box sx={{ paddingBottom: "24px" }}>
            <HeaderValuePair
              component="h2"
              title="Previous participant ID"
              variant_title="display6"
              fontFamily="Roboto Mono"
              value={studyParticipant.getSponsorParticipantId()}
            />
          </Box>
          <Box sx={{ paddingBottom: "24px", width: "100%" }}>
            <Typography component="h2" variant="display6">
              New participant ID
            </Typography>
            <TextField
              label="Participant ID (required)"
              value={newParticipantId}
              placeholder="XXXXX"
              error={newIdTextFieldError}
              helperText={newIdTextFieldHelperText}
              onChange={(e) => {
                setNewIdTextFieldError(false);
                setNewIdTextFieldHelperText("");
                setNewParticipantId(e.target.value);
              }}
              inputProps={{
                style: { fontFamily: "Roboto Mono" },
              }}
              sx={{
                marginTop: "8px",
                display: "flex",
                justifySelf: "start",
              }}
            ></TextField>
          </Box>
        </>
      ),
      onPrevious: () => {
        setStepId(prevStepId);
        setPrevStepId(-1);
      },
      isPreviousDisabled: false,
      nextButtonText: "Next",
      isNextButtonDisabled: false,
      isNextButtonLoading: validateLoading,
      onNext: async () => {
        setValidateLoading(true);
        if (await validateParticipantId()) {
          setPrevStepId(EditParticipantIdSteps.EditParticipantId);
          setStepId(EditParticipantIdSteps.ConfirmParticipantId);
        }
        setValidateLoading(false);
      },
    },
    {
      id: EditParticipantIdSteps.ConfirmParticipantId,
      page: (
        <>
          <Typography
            ref={sectionRef}
            tabIndex={-1}
            component="h2"
            variant="display6"
          >
            Confirm participant ID change
          </Typography>
          <Box sx={{ padding: "24px 0px" }}>
            <HeaderValuePair
              component="h3"
              title="Previous participant ID"
              fontFamily="Roboto Mono"
              value={studyParticipant.getSponsorParticipantId()}
            />
          </Box>
          <Box>
            <HeaderValuePair
              component="h3"
              title="New participant ID"
              fontFamily="Roboto Mono"
              value={newParticipantId}
            />
          </Box>
          <Box sx={{ padding: "24px 0px" }}>
            <HeaderValuePair
              component="h3"
              title="Update Reason"
              value={selectedReason.label}
            />
          </Box>
        </>
      ),
      onPrevious: () => {
        setStepId(prevStepId);
        setPrevStepId(EditParticipantIdSteps.EditReason);
      },
      isPreviousDisabled: false,
      nextButtonText: "Save",
      isNextButtonDisabled: false,
      isNextButtonLoading: editLoading,
      onNext: async () => {
        setEditLoading(true);
        await editParticipantId();
        setEditLoading(false);
      },
    },
  ];

  const steps: Steps = new Steps(stepInfo, EditParticipantIdSteps.EditReason);
  const [prevStepId, setPrevStepId] = useState<number>(-1);
  const [stepId, setStepId] = useState<number>(steps.default.id);

  const [showDuplicateParticipantDialog, setShowDuplicateParticipantDialog] =
    useState<boolean>(false);
  const [showRedirectDialog, setShowRedirectDialog] = useState<boolean>(false);
  const [showContactVerilyDialog, setShowContactVerilyDialog] =
    useState<boolean>(false);

  useEffect(() => {
    // Focus on the section title if exists, otherwise focus on the header title
    setFocusOnHeaderTitle(false);
    if (sectionRef.current) {
      focusOnRef(sectionRef);
    } else {
      setFocusOnHeaderTitle(true);
    }
  }, [stepId]);

  useEffect(() => {
    setSelectedReason(editReasonSelection.default);
    setPrevStepId(-1);
    setStepId(steps.default.id);
    setNewParticipantId("");
    setNewIdTextFieldError(false);
    setNewIdTextFieldHelperText("");
    setValidateLoading(false);
    setEditLoading(false);
    setErrorSnackbarMessage("");
    setShowErrorSnackbar(false);
  }, [steps.default.id]);

  const validateParticipantId = useCallback(async () => {
    const newParticipantIdTrimmed = newParticipantId.trim();
    setNewParticipantId(newParticipantIdTrimmed);
    if (newParticipantIdTrimmed.length === 0) {
      setNewIdTextFieldError(true);
      setNewIdTextFieldHelperText("Participant ID Required");
      return false;
    }

    if (!isAuthenticated || !auth0Config) {
      setError("User not authenticated. Please logout and login again.");

      return false;
    }

    const isAvailableParticipantId = await (async () => {
      try {
        const token = await getAccessTokenSilently({
          audience: auth0Config.audience,
        });

        const client = new StudyParticipantServiceApiClient(
          auth0Config.audience!,
          token
        );

        await client.lookupSponsorParticipant(
          new LookupSponsorParticipantRequest()
            .setRegistryId(currentRegistryId)
            .setSponsorParticipantId(newParticipantIdTrimmed)
        );

        // If lookupSponsorParticipant doesn't throw a NOT_FOUND error,
        // then the participant ID is already in use
        setShowDuplicateParticipantDialog(true);
        return false;
      } catch (error) {
        let errMsg = "Failed to update participant ID. Please try again.";
        if (
          error instanceof RpcError &&
          error.code === StatusCode.PERMISSION_DENIED
        ) {
          errMsg = "User is not authorized to update participant.";
        } else if (
          error instanceof RpcError &&
          error.code === StatusCode.NOT_FOUND
        ) {
          // We want the participant to be missing
          return true;
        }

        telemetry.logError(error);
        setError(errMsg);
      }
    })();

    return isAvailableParticipantId;
  }, [
    auth0Config,
    getAccessTokenSilently,
    isAuthenticated,
    currentRegistryId,
    newParticipantId,
    setError,
  ]);

  const editParticipantId = useCallback(async () => {
    if (!isAuthenticated || !auth0Config) {
      setError("User not authenticated. Please logout and login again.");
      return;
    }

    await (async () => {
      try {
        const token = await getAccessTokenSilently({
          audience: auth0Config.audience,
        });

        const client = new StudyParticipantServiceApiClient(
          auth0Config.audience!,
          token
        );

        await client.updateStudyParticipant(
          new UpdateStudyParticipantRequest()
            .setStudyParticipant(
              new StudyParticipant()
                .setName(studyParticipant.getName())
                .setSponsorParticipantId(newParticipantId.trim())
            )
            .setUpdateMask(
              new FieldMask().setPathsList(["sponsor_participant_id"])
            )
            .setUpdateReason(selectedReason.label)
        );

        dispatch(setSuccessMessage("Participant ID updated"));
        exitPage();
      } catch (error) {
        let errMsg = "Failed to update participant ID. Please try again.";
        if (
          error instanceof RpcError &&
          error.code === StatusCode.PERMISSION_DENIED
        ) {
          errMsg = "User is not authorized to update participant.";
        } else if (
          error instanceof RpcError &&
          error.code === StatusCode.NOT_FOUND
        ) {
          errMsg = "Participant not found.";
        }

        telemetry.logError(error);
        setError(errMsg);
      }
    })();
  }, [
    auth0Config,
    getAccessTokenSilently,
    isAuthenticated,
    newParticipantId,
    studyParticipant,
    selectedReason,
    dispatch,
    exitPage,
    setError,
  ]);

  const containerRef = React.useRef<HTMLDivElement>(null);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "100vh",
        }}
      >
        <StepsHeader
          scrollTarget={containerRef.current ?? undefined}
          title="Edit participant ID"
          stepId={stepId}
          steps={steps}
          onClose={exitPage}
          focusOnTitle={focusOnHeaderTitle}
        />
        <Container
          role="main"
          ref={containerRef}
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "40%",
            height: "100%",
            alignItems: "start",
            padding: "48px 0px 24px",
            overflowY: "auto",
          }}
        >
          {steps.get(stepId)!.page}
          <Box sx={{ marginTop: "64px", flexGrow: 1 }}></Box>
          <Footer
            leftButtonText="Previous"
            isLeftButtonDisabled={steps.get(stepId)!.isPreviousDisabled}
            handleClickLeftButton={steps.get(stepId)!.onPrevious}
            rightButtonText={steps.get(stepId)!.nextButtonText}
            isRightButtonDisabled={steps.get(stepId)!.isNextButtonDisabled}
            isRightButtonLoading={steps.get(stepId)!.isNextButtonLoading}
            handleClickRightButton={steps.get(stepId)!.onNext}
          />
        </Container>
      </Box>
      <DuplicateParticipantFoundDialog
        open={showDuplicateParticipantDialog}
        onClose={() => {
          setShowDuplicateParticipantDialog(false);
        }}
      />
      <ManageWatchRedirectDialog
        open={showRedirectDialog}
        onExit={() => {
          setShowRedirectDialog(false);
          exitPage();
        }}
        onCancel={() => {
          setShowRedirectDialog(false);
        }}
      />
      <ContactVerilyDialog
        open={showContactVerilyDialog}
        onClose={() => {
          setShowContactVerilyDialog(false);
        }}
      />
      <SDPErrorSnackbar
        showSnackbar={showErrorSnackbar}
        setShowSnackbar={setShowErrorSnackbar}
        errorMessage={errorSnackbarMessage}
      />
    </>
  );
};

export default EditParticipantId;
